import { cn } from "@repo/ui/lib/utils";
import type { ReactNode } from "react";
import { type TimelineSurface, timelineBlockSurface, timelineSubtext } from "@/shared/timeline";
import styles from "./schedule-slot-card.module.css";

/** The calendar/Gantt button owns interaction; this component owns its surface. */
export const SCHEDULE_SLOT_EVENT_CLASS =
  "rounded-md p-0! bg-transparent! inset-ring-0! transition-shadow hover:shadow-sm data-selected:ring-2 data-selected:ring-primary/50";

interface ScheduleSlotProps extends TimelineSurface {
  title: ReactNode;
  time: ReactNode;
  label?: string;
}

function Body({ title, time, label, variant, accent }: ScheduleSlotProps) {
  return (
    <div className={styles.content}>
      <div className={styles.heading}>
        <span className="min-w-0 truncate text-[13px] font-semibold leading-4">{title}</span>
        {label && <span className={styles.label}>{label}</span>}
      </div>
      <div className={cn(styles.time, "tabular-nums", timelineSubtext({ variant, accent }))}>{time}</div>
    </div>
  );
}

/**
 * A booked slot on the day/week grid or the resource Gantt. It takes the height
 * its track hands it and reflows by container query, which is why it composes
 * the timeline surface rather than a plan block.
 */
export function ScheduleSlotTile({ title, time, label, variant, accent }: ScheduleSlotProps) {
  return (
    <div
      className={cn(styles.card, "relative h-full w-full min-w-0 text-left", timelineBlockSurface({ variant, accent }))}
    >
      <Body title={title} time={time} label={label} variant={variant} accent={accent} />
    </div>
  );
}

/** The month grid's one-line variant, where a slot gets a row rather than a span. */
export function ScheduleSlotChip({ title, time, label, variant, accent }: ScheduleSlotProps) {
  return (
    <div
      className={cn(
        styles.card,
        styles.compact,
        "relative h-full w-full min-w-0 text-left",
        timelineBlockSurface({ variant, accent }),
      )}
    >
      <Body title={title} time={time} label={label} variant={variant} accent={accent} />
    </div>
  );
}
